import { useState } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { SessionProvider, useSession } from './lib/session';
import { short } from './lib/fmt';
import { Button } from './components/ui';
import { Loading, Bone, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton } from './components/skeleton';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Capture from './pages/Capture';
import Claims from './pages/Claims';
import Accounts from './pages/Accounts';
import AccountStatement from './pages/AccountStatement';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import Payroll from './pages/Payroll';
import Projection from './pages/Projection';
import Bills from './pages/Bills';
import Retainers from './pages/Retainers';
import TallyExport from './pages/TallyExport';
import Users from './pages/Users';

/** label, path, capability, glyph */
const NAV: [string, string, string, string][] = [
  ['Dashboard',   '/',            'report:read',      '▤'],
  ['Add expense', '/capture',     'expense:submit',   '＋'],
  ['Expenses',    '/expenses',    'expense:read_own', '▽'],
  ['Accounts',    '/accounts',    'cash:read_own',    '◫'],
  ['Bills',       '/bills',       'expense:read_all', '⏱'],
  ['Payroll',     '/payroll',     'payroll:read',     '☰'],
  ['Projection',  '/projection',  'report:financial', '◈'],
  ['Invoices',    '/invoices',    'invoice:read',     '▦'],
  ['Retainers',   '/retainers',   'invoice:read',     '↻'],
  ['Tally',       '/tally',       'tally:export',     '⇪'],
  ['Users',       '/users',       'user:manage',      '⚿'],
];

/**
 * The frame, before the session has come back.
 *
 * This is the first paint of the whole application, so it draws the chrome it
 * is about to have — the brand strip, the sidebar, a page head and a first
 * section — rather than a bare word on an empty ground. Which screen follows is
 * not known yet, so the body is the shape every screen shares.
 */
function ShellSkeleton() {
  return (
    <Loading label="Loading Cocoma Books">
      <div className="min-h-full">
        <div className="h-[3px] bg-brand sticky top-0 z-50" />
        <div className="grid grid-cols-1 md:grid-cols-[236px_1fr] min-h-[calc(100vh-3px)]">
          <aside className="hidden md:flex bg-card md:border-r border-rule flex-col gap-5 py-5">
            <div className="flex items-center gap-3 px-5">
              <Bone w={28} h={28} radius={6} />
              <div className="flex-1">
                <Bone w="70%" h={12} />
                <Bone w="40%" h={9} className="mt-1.5" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 px-3">
              {Array.from({ length: 8 }, (_, i) => <Bone key={i} h={30} radius={4} />)}
            </div>
          </aside>
          <main className="px-4 md:px-8 py-6 md:py-7 max-w-[1280px] min-w-0">
            <HeadSkeleton actions={1} />
            <TilesSkeleton />
            <CardSkeleton flush><TableSkeleton rows={5} cols={5} /></CardSkeleton>
          </main>
        </div>
      </div>
    </Loading>
  );
}

function Shell() {
  const { user, meta, fy, setFy, can, signOut, loading, mustChangePassword } = useSession();
  const location = useLocation();
  const [sheet, setSheet] = useState(false);

  if (loading) return <ShellSkeleton />;
  if (!user || mustChangePassword) return <Login />;

  const visible = NAV.filter(([, , cap]) => can(cap));
  const home = visible[0]?.[1] ?? '/capture';
  const roleLabel = meta?.roles.find((r) => r.key === user.role)?.label ?? user.role;

  // On a phone the sidebar would be a full screen of chrome before the first
  // number, so it is replaced by a slim bar at the top and a tab bar at the
  // bottom — where a thumb already is. The sidebar returns from md up, which
  // covers iPad portrait onwards.
  const tabs = visible.slice(0, 4);
  const rest = visible.slice(4);

  return (
    <div className="min-h-full">
      <div className="h-[3px] bg-brand sticky top-0 z-50" />

      <MobileBar org={meta?.org?.trade_name} fy={fy} onMenu={() => setSheet(true)} />

      <div className="grid grid-cols-1 md:grid-cols-[236px_1fr] min-h-[calc(100vh-3px)]">
        <aside className="no-print hidden md:flex bg-card md:border-r border-rule
                          md:sticky md:top-[3px] md:h-[calc(100vh-3px)] overflow-auto
                          flex-col gap-5 py-5">
          <div className="flex items-center gap-3 px-5">
            <span className="grid place-items-center w-7 h-7 rounded-md bg-brand text-brand-ink
                             font-display font-bold text-[17px] shrink-0">C</span>
            <span className="flex flex-col leading-tight min-w-0">
              <b className="font-display text-[14.5px] font-semibold">Cocoma Books</b>
              <em className="not-italic font-mono text-[10.5px] uppercase tracking-[0.09em] text-muted">
                {fy ? `FY ${fy}` : 'Books'}
              </em>
            </span>
          </div>

          <nav className="flex flex-col gap-px px-3">
            {visible.map(([label, path, , ico]) => (
              <NavLink key={path} to={path} end={path === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-2.5 py-2 rounded text-[13.5px] font-medium no-underline
                   ${isActive ? 'bg-card-2 text-ink' : 'text-ink-2 hover:bg-card-2 hover:text-ink'}`}>
                <span className="w-4 text-center text-xs text-muted">{ico}</span>
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto px-4 flex flex-col gap-3">
            {meta && meta.fys.length > 0 && can('invoice:read') && (
              <label className="flex flex-col gap-1.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">Financial year</span>
                <select value={fy ?? ''} onChange={(e) => setFy(e.target.value)}
                        className="w-full rounded border border-rule bg-card px-2.5 py-2 text-[13.5px]">
                  {meta.fys.map((f) => <option key={f} value={f}>FY {f}</option>)}
                </select>
              </label>
            )}
            {can('expense:submit') && (
              <NavLink to="/capture"
                className="inline-flex items-center justify-center rounded bg-brand text-brand-ink
                           font-semibold text-[13px] px-3.5 py-2.5 no-underline hover:brightness-95">
                Add an expense
              </NavLink>
            )}
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-rule-soft">
              <div className="flex flex-col leading-tight min-w-0">
                <b className="text-[13px] font-semibold truncate">{user.name}</b>
                <span className="font-mono text-[10.5px] uppercase tracking-[0.07em] text-muted truncate">
                  {roleLabel}
                </span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => void signOut()}>Sign out</Button>
            </div>
            {meta?.org && (
              <p className="font-mono text-[10.5px] leading-relaxed text-muted m-0">
                {meta.org.trade_name} · GSTIN {meta.org.gstin}
              </p>
            )}
          </div>
        </aside>

        <main className="px-4 md:px-8 py-6 md:py-7 pb-[calc(76px+env(safe-area-inset-bottom))] md:pb-16 max-w-[1280px] min-w-0">
          <Routes>
            <Route path="/" element={can('report:read') ? <Dashboard /> : <Navigate to={home} replace />} />
            <Route path="/capture" element={<Guard cap="expense:submit"><Capture /></Guard>} />
            <Route path="/expenses" element={<Guard cap="expense:read_own"><Claims /></Guard>} />
            <Route path="/accounts" element={<Guard cap="cash:read_own"><Accounts /></Guard>} />
            <Route path="/accounts/:id" element={<Guard cap="cash:read_own"><AccountStatement /></Guard>} />
            <Route path="/bills" element={<Guard cap="expense:read_all"><Bills /></Guard>} />
            <Route path="/payroll" element={<Guard cap="payroll:read"><Payroll /></Guard>} />
            <Route path="/payroll/:id" element={<Guard cap="payroll:read"><Payroll /></Guard>} />
            <Route path="/projection" element={<Guard cap="report:financial"><Projection /></Guard>} />
            <Route path="/invoices" element={<Guard cap="invoice:read"><Invoices /></Guard>} />
            <Route path="/invoices/:id" element={<Guard cap="invoice:read"><InvoiceDetail /></Guard>} />
            <Route path="/retainers" element={<Guard cap="invoice:read"><Retainers /></Guard>} />
            <Route path="/tally" element={<Guard cap="tally:export"><TallyExport /></Guard>} />
            <Route path="/users" element={<Guard cap="user:manage"><Users /></Guard>} />
            <Route path="*" element={<Navigate to={home} replace state={{ from: location.pathname }} />} />
          </Routes>
        </main>
      </div>

      <TabBar tabs={tabs} hasMore={rest.length > 0} onMore={() => setSheet(true)} />

      {sheet && (
        <MoreSheet items={visible} meta={meta} fy={fy} setFy={setFy} user={user}
                   roleLabel={roleLabel} onClose={() => setSheet(false)}
                   onSignOut={() => void signOut()} />
      )}
    </div>
  );
}

/** The phone's top bar: who and when, nothing else. Hidden from md up. */
function MobileBar({ org, fy, onMenu }: { org?: string; fy: string | null; onMenu: () => void }) {
  return (
    <header className="no-print md:hidden sticky top-[3px] z-40 flex items-center gap-3
                       bg-card border-b border-rule px-4 h-14">
      <span className="grid place-items-center w-7 h-7 rounded-md bg-brand text-brand-ink
                       font-display font-bold text-[16px] shrink-0">C</span>
      <span className="flex flex-col leading-tight min-w-0 flex-1">
        <b className="font-display text-[14px] font-semibold truncate">{org ?? 'Cocoma Books'}</b>
        <em className="not-italic font-mono text-[10px] uppercase tracking-[0.09em] text-muted">
          {fy ? `FY ${fy}` : 'Books'}
        </em>
      </span>
      <button type="button" onClick={onMenu} aria-label="Menu"
        className="grid place-items-center w-11 h-11 -mr-2 rounded text-ink-2 hover:bg-card-2 cursor-pointer">
        <span className="text-[18px] leading-none">☰</span>
      </button>
    </header>
  );
}

/**
 * The phone's tab bar. Four destinations at the bottom, where the thumb is,
 * each a 56px target — comfortably above the 44px minimum a finger needs.
 */
function TabBar(
  { tabs, hasMore, onMore }:
  { tabs: [string, string, string, string][]; hasMore: boolean; onMore: () => void },
) {
  return (
    <nav className="no-print md:hidden fixed bottom-0 inset-x-0 z-40 flex
                    bg-card border-t border-rule pb-[env(safe-area-inset-bottom)]">
      {tabs.map(([label, path, , ico]) => (
        <NavLink key={path} to={path} end={path === '/'}
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center gap-1 min-h-[60px] no-underline
             ${isActive ? 'text-ink' : 'text-muted'}`}>
          <span className="text-[17px] leading-none">{ico}</span>
          <span className="text-[10px] font-medium leading-none truncate max-w-full px-1">{label}</span>
        </NavLink>
      ))}
      {hasMore && (
        <button type="button" onClick={onMore}
          className="flex-1 flex flex-col items-center justify-center gap-1 min-h-[60px]
                     text-muted cursor-pointer">
          <span className="text-[17px] leading-none">⋯</span>
          <span className="text-[10px] font-medium leading-none">More</span>
        </button>
      )}
    </nav>
  );
}

/** Everything the tab bar could not hold, as a sheet that slides up from it. */
function MoreSheet(
  { items, meta, fy, setFy, user, roleLabel, onClose, onSignOut }:
  { items: [string, string, string, string][]; meta: ReturnType<typeof useSession>['meta'];
    fy: string | null; setFy: (v: string) => void;
    user: NonNullable<ReturnType<typeof useSession>['user']>; roleLabel: string;
    onClose: () => void; onSignOut: () => void },
) {
  return (
    <div className="no-print md:hidden fixed inset-0 z-50 flex flex-col justify-end">
      <button type="button" aria-label="Close" onClick={onClose}
              className="absolute inset-0 bg-black/50 cursor-pointer" />
      <div className="relative bg-card rounded-t-2xl border-t border-rule max-h-[85vh] overflow-auto
                      pb-[calc(12px+env(safe-area-inset-bottom))]">
        <div className="sticky top-0 bg-card flex items-center justify-between px-4 h-14 border-b border-rule-soft">
          <b className="font-display text-[15px]">{user.name} · <span className="font-normal text-muted">{roleLabel}</span></b>
          <button type="button" onClick={onClose} aria-label="Close"
            className="grid place-items-center w-11 h-11 -mr-2 rounded text-ink-2 cursor-pointer">✕</button>
        </div>

        <div className="grid grid-cols-3 gap-2 p-4">
          {items.map(([label, path, , ico]) => (
            <NavLink key={path} to={path} end={path === '/'} onClick={onClose}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-2 rounded-xl border px-2 py-4 min-h-[84px]
                 no-underline text-center ${isActive
                   ? 'border-brand bg-brand/10 text-ink' : 'border-rule bg-card text-ink-2'}`}>
              <span className="text-[22px] leading-none">{ico}</span>
              <span className="text-[11px] font-medium leading-tight">{label}</span>
            </NavLink>
          ))}
        </div>

        <div className="px-4 pb-2 flex flex-col gap-3">
          {meta && meta.fys.length > 0 && (
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">Financial year</span>
              <select value={fy ?? ''} onChange={(e) => setFy(e.target.value)}
                      className="w-full rounded-lg border border-rule bg-card px-3 min-h-[48px] text-[16px]">
                {meta.fys.map((f) => <option key={f} value={f}>FY {f}</option>)}
              </select>
            </label>
          )}
          <Button block onClick={onSignOut}>Sign out</Button>
          {meta?.org && (
            <p className="font-mono text-[10.5px] leading-relaxed text-muted m-0 text-center">
              {meta.org.trade_name} · GSTIN {meta.org.gstin}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Refuses a screen this person's role does not include, and says so plainly. */
function Guard({ cap, children }: { cap: string; children: React.ReactNode }) {
  const { can } = useSession();
  if (can(cap)) return <>{children}</>;
  return (
    <div className="bg-card border border-rule rounded p-6">
      <h2 className="font-display text-lg font-semibold m-0">Not your part of the system</h2>
      <p className="mt-2 text-[13.5px] text-ink-2">
        Your access does not include this screen. If you need it, ask the owner to widen your role.
      </p>
    </div>
  );
}

export default function App() {
  return <SessionProvider><Shell /></SessionProvider>;
}

export { short };
